import { Vector3 } from 'three';
import {
  ALL_SURFACE_IDS,
  faceNameOf,
  Surface,
  type SurfaceEdge,
  type SurfaceHole,
  type SurfaceId,
  type SurfaceSegment,
} from './Surface';

/** Scratch for hole tests on the hot path, so the room never allocates. */
const scratchLocal = { u: 0, v: 0 };

export interface ObstacleBox {
  center: Vector3;
  halfSize: Vector3;
  magnetic: boolean;
  /** Visual/gameplay class: crate, pillar, console, reactor... */
  kind?: string;
  /** The zone the obstacle belongs to, when the level has zones. */
  zoneId?: string;
}

/** A rectangular zone of a level: a floor centre, size, and six surfaces. */
export interface ZoneInput {
  id: string;
  origin: Vector3;
  size: Vector3;
  magnetic?: Partial<Record<SurfaceId, boolean>>;
  enabled?: Partial<Record<SurfaceId, boolean>>;
  /** Faces of this zone that are exterior hull. */
  hullFaces?: SurfaceId[];
}

/** A doorway between two zones, authored in world space. */
export interface PortalInput {
  id: string;
  /** The two zones the doorway joins. */
  a: string;
  b: string;
  /** The wall the doorway sits in runs along this horizontal axis. */
  axis: 'x' | 'z';
  /** Door centre in world space, on the shared wall. */
  position: Vector3;
  /** Door opening width along the wall. */
  width: number;
  /** Door opening height from the floor. */
  height: number;
}

/** A resolved doorway, exposed for the mesh builder. */
export interface Portal {
  id: string;
  aZone: string;
  bZone: string;
  wallA: Surface;
  wallB: Surface;
  holeA: SurfaceHole;
  holeB: SurfaceHole;
}

interface FloorSegment {
  axis: 'u' | 'v';
  value: number;
  alongMin: number;
  alongMax: number;
}

export class Room {
  readonly surfaces = new Map<string, Surface>();
  readonly obstacles: ObstacleBox[] = [];
  readonly zones: ZoneInput[] = [];
  readonly portals: Portal[] = [];
  readonly size: Vector3;
  /** True when surface keys are namespaced by zone (`zone:face`). */
  private readonly keyed: boolean;
  private defaultZone: string;

  constructor(
    readonly id: string,
    width: number,
    height: number,
    depth: number,
    options: {
      magnetic?: Partial<Record<SurfaceId, boolean>>;
      enabled?: Partial<Record<SurfaceId, boolean>>;
      obstacles?: ObstacleBox[];
      zones?: ZoneInput[];
      portals?: PortalInput[];
    } = {},
  ) {
    this.size = new Vector3(width, height, depth);
    this.keyed = (options.zones?.length ?? 0) > 0;
    this.defaultZone = options.zones?.[0]?.id ?? id;

    if (this.keyed) {
      // Register every zone before building any of them: partitioning a shared
      // wall needs to know all the neighbours up front.
      for (const zone of options.zones ?? []) this.zones.push(zone);
      for (const zone of this.zones) this.buildZone(zone);
    } else {
      this.buildZone({
        id,
        origin: new Vector3(0, 0, 0),
        size: new Vector3(width, height, depth),
        magnetic: options.magnetic,
        enabled: options.enabled,
      });
    }

    for (const obstacle of options.obstacles ?? []) this.obstacles.push(obstacle);
    for (const portal of options.portals ?? []) this.addPortal(portal);
  }

  /** The key a face of a zone is stored under. */
  keyFor(zoneId: string, face: SurfaceId): string {
    return this.keyed ? `${zoneId}:${face}` : face;
  }

  private buildZone(zone: ZoneInput): void {
    const w = zone.size.x;
    const h = zone.size.y;
    const d = zone.size.z;
    const halfW = w / 2;
    const halfD = d / 2;
    const ox = zone.origin.x;
    const oy = zone.origin.y;
    const oz = zone.origin.z;

    const enabled = (face: SurfaceId): boolean => zone.enabled?.[face] ?? true;
    const magnetic = (face: SurfaceId): boolean => zone.magnetic?.[face] ?? true;
    const hullFaces = new Set<SurfaceId>(zone.hullFaces ?? []);

    const add = (
      face: SurfaceId,
      normal: Vector3,
      forward: Vector3,
      origin: Vector3,
      uMin: number,
      uMax: number,
      vMin: number,
      vMax: number,
    ): void => {
      if (!enabled(face)) return;
      const key = this.keyFor(zone.id, face);
      const surface = new Surface(
        face,
        normal,
        forward,
        origin,
        uMin,
        uMax,
        vMin,
        vMax,
        magnetic(face),
        key,
        zone.id,
        hullFaces.has(face),
      );
      surface.setSegments(this.partitionFace(zone, face, surface));
      this.surfaces.set(key, surface);
    };

    // floor: y = oy, up +Y, forward -Z, right +X
    add(
      'floor',
      new Vector3(0, 1, 0),
      new Vector3(0, 0, -1),
      new Vector3(ox, oy, oz),
      -halfW,
      halfW,
      -halfD,
      halfD,
    );
    // ceiling: y = oy + h, up -Y, forward -Z, right -X
    add(
      'ceiling',
      new Vector3(0, -1, 0),
      new Vector3(0, 0, -1),
      new Vector3(ox, oy + h, oz),
      -halfW,
      halfW,
      -halfD,
      halfD,
    );
    // left wall: x = ox - halfW, up +X, forward -Z, right -Y
    add(
      'left',
      new Vector3(1, 0, 0),
      new Vector3(0, 0, -1),
      new Vector3(ox - halfW, oy, oz),
      -h,
      0,
      -halfD,
      halfD,
    );
    // right wall: x = ox + halfW, up -X, forward -Z, right +Y
    add(
      'right',
      new Vector3(-1, 0, 0),
      new Vector3(0, 0, -1),
      new Vector3(ox + halfW, oy, oz),
      0,
      h,
      -halfD,
      halfD,
    );
    // front wall: z = oz - halfD, up +Z, forward +Y, right +X
    add(
      'front',
      new Vector3(0, 0, 1),
      new Vector3(0, 1, 0),
      new Vector3(ox, oy, oz - halfD),
      -halfW,
      halfW,
      0,
      h,
    );
    // back wall: z = oz + halfD, up -Z, forward +Y, right -X
    add(
      'back',
      new Vector3(0, 0, -1),
      new Vector3(0, 1, 0),
      new Vector3(ox, oy, oz + halfD),
      -halfW,
      halfW,
      0,
      h,
    );

    this.buildEdges(zone);
  }

  private buildEdges(zone: ZoneInput): void {
    const w = zone.size.x;
    const h = zone.size.y;
    const d = zone.size.z;
    const halfW = w / 2;
    const halfD = d / 2;
    const isEnabled = (face: SurfaceId): boolean => zone.enabled?.[face] ?? true;
    const key = (face: SurfaceId): string => this.keyFor(zone.id, face);

    const edge = (
      owner: SurfaceId,
      neighbour: SurfaceId,
      axis: 'u' | 'v',
      value: number,
      alongMin: number,
      alongMax: number,
      sign: 1 | -1,
    ): void => {
      if (!isEnabled(owner) || !isEnabled(neighbour)) return;
      this.surfaces.get(key(owner))?.edges.push({
        neighbor: key(neighbour),
        axis,
        value,
        alongMin,
        alongMax,
        sign,
        kind: 'face',
      });
    };

    // floor
    edge('floor', 'left', 'u', -halfW, -halfD, halfD, -1);
    edge('floor', 'right', 'u', halfW, -halfD, halfD, 1);
    edge('floor', 'front', 'v', halfD, -halfW, halfW, 1);
    edge('floor', 'back', 'v', -halfD, -halfW, halfW, -1);
    // ceiling (u = -x)
    edge('ceiling', 'left', 'u', halfW, -halfD, halfD, 1);
    edge('ceiling', 'right', 'u', -halfW, -halfD, halfD, -1);
    edge('ceiling', 'front', 'v', halfD, -halfW, halfW, 1);
    edge('ceiling', 'back', 'v', -halfD, -halfW, halfW, -1);
    // left wall (u = -y)
    edge('left', 'floor', 'u', 0, -halfD, halfD, 1);
    edge('left', 'ceiling', 'u', -h, -halfD, halfD, -1);
    edge('left', 'front', 'v', halfD, -h, 0, 1);
    edge('left', 'back', 'v', -halfD, -h, 0, -1);
    // right wall (u = +y)
    edge('right', 'floor', 'u', 0, -halfD, halfD, -1);
    edge('right', 'ceiling', 'u', h, -halfD, halfD, 1);
    edge('right', 'front', 'v', halfD, 0, h, 1);
    edge('right', 'back', 'v', -halfD, 0, h, -1);
    // front wall (v = +y)
    edge('front', 'floor', 'v', 0, -halfW, halfW, -1);
    edge('front', 'ceiling', 'v', h, -halfW, halfW, 1);
    edge('front', 'left', 'u', -halfW, 0, h, -1);
    edge('front', 'right', 'u', halfW, 0, h, 1);
    // back wall (v = +y)
    edge('back', 'floor', 'v', 0, -halfW, halfW, -1);
    edge('back', 'ceiling', 'v', h, -halfW, halfW, 1);
    edge('back', 'right', 'u', -halfW, 0, h, -1);
    edge('back', 'left', 'u', halfW, 0, h, 1);
  }

  // ------------------------------------------------------------------ shared walls

  /**
   * The pieces of a face this zone actually owns.
   *
   * Where a later zone butts against this face, the shared rectangle belongs to
   * that zone: this one keeps only the rest. Ownership always falls to the later
   * zone in the level definition, so exactly one of the two coincident faces is
   * drawn and one of them is the hull. Without this the hull's spine wall and
   * the bay wall are both drawn over the middle band, which z-fights and lets a
   * shot at an interior partition breach the ship.
   */
  private partitionFace(zone: ZoneInput, face: SurfaceId, surface: Surface): SurfaceSegment[] {
    let rects: SurfaceHole[] = [
      { uMin: surface.uMin, uMax: surface.uMax, vMin: surface.vMin, vMax: surface.vMax },
    ];
    const index = this.zones.findIndex((candidate) => candidate.id === zone.id);
    for (let i = index + 1; i < this.zones.length; i++) {
      const cover = this.coverRect(zone, this.zones[i], face, surface);
      if (!cover) continue;
      rects = rects.flatMap((rect) => subtractRect(rect, cover));
    }
    return rects.map((rect) => ({ ...rect, hull: surface.hull }));
  }

  /** The rectangle a neighbour zone covers on this face, in local u/v, or null. */
  private coverRect(
    zone: ZoneInput,
    other: ZoneInput,
    face: SurfaceId,
    surface: Surface,
  ): SurfaceHole | null {
    const plane = facePlane(zone, face);
    if (!plane) return null;
    const otherMin = zoneMin(other, plane.axis);
    const otherMax = zoneMax(other, plane.axis);
    const touches =
      plane.beyond < 0 ? Math.abs(otherMax - plane.at) < EPS : Math.abs(otherMin - plane.at) < EPS;
    if (!touches) return null;

    // Project the neighbour's box onto the face and clip it to the face.
    let uMin = Infinity;
    let uMax = -Infinity;
    let vMin = Infinity;
    let vMax = -Infinity;
    for (let corner = 0; corner < 8; corner++) {
      scratchCorner.set(
        corner & 1 ? zoneMax(other, 'x') : zoneMin(other, 'x'),
        corner & 2 ? zoneMax(other, 'y') : zoneMin(other, 'y'),
        corner & 4 ? zoneMax(other, 'z') : zoneMin(other, 'z'),
      );
      surface.toLocal(scratchCorner, scratchLocalPoint);
      uMin = Math.min(uMin, scratchLocalPoint.u);
      uMax = Math.max(uMax, scratchLocalPoint.u);
      vMin = Math.min(vMin, scratchLocalPoint.v);
      vMax = Math.max(vMax, scratchLocalPoint.v);
    }
    uMin = Math.max(uMin, surface.uMin);
    uMax = Math.min(uMax, surface.uMax);
    vMin = Math.max(vMin, surface.vMin);
    vMax = Math.min(vMax, surface.vMax);
    if (uMax - uMin < EPS || vMax - vMin < EPS) return null;
    return { uMin, uMax, vMin, vMax };
  }

  // ------------------------------------------------------------------ portals

  private addPortal(portal: PortalInput): void {
    const wallA = this.findPortalWall(portal.a, portal);
    const wallB = this.findPortalWall(portal.b, portal);
    if (!wallA || !wallB) return;

    const holeA = this.holeForWall(wallA, portal);
    const holeB = this.holeForWall(wallB, portal);
    wallA.addHole(holeA);
    wallB.addHole(holeB);

    const floorA = this.surfaces.get(this.keyFor(portal.a, 'floor'));
    const floorB = this.surfaces.get(this.keyFor(portal.b, 'floor'));
    this.portals.push({
      id: portal.id,
      aZone: portal.a,
      bZone: portal.b,
      wallA,
      wallB,
      holeA,
      holeB,
    });
    if (!floorA || !floorB) return;

    const segmentA = this.doorFloorSegment(wallA, holeA);
    const segmentB = this.doorFloorSegment(wallB, holeB);
    if (!segmentA || !segmentB) return;

    this.addPortalEdge(floorA, segmentA, floorB, segmentB);
    this.addPortalEdge(floorB, segmentB, floorA, segmentA);
  }

  private findPortalWall(zoneId: string, portal: PortalInput): Surface | undefined {
    const candidates: SurfaceId[] = portal.axis === 'x' ? ['front', 'back'] : ['left', 'right'];
    let best: Surface | undefined;
    let bestDistance = Infinity;
    for (const face of candidates) {
      const surface = this.surfaces.get(this.keyFor(zoneId, face));
      if (!surface) continue;
      const distance = Math.abs(surface.signedDistance(portal.position));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = surface;
      }
    }
    // An axis-aligned doorway should sit on its wall. If the nearest wall is
    // more than half a metre away the authoring is wrong, but we still fall
    // back to the closest candidate so the level degrades gracefully.
    return best;
  }

  private holeForWall(wall: Surface, portal: PortalInput): SurfaceHole {
    const centre = wall.toLocal(portal.position);
    const zone = this.zones.find((entry) => entry.id === wall.zoneId);
    const floorY = zone?.origin.y ?? 0;
    const floorLocal = wall.toLocal(new Vector3(portal.position.x, floorY, portal.position.z));

    const uIsVertical = Math.abs(wall.right.y) > Math.abs(wall.forward.y);
    const verticalAxis: 'u' | 'v' = uIsVertical ? 'u' : 'v';
    const floorVertical = floorLocal[verticalAxis];
    const verticalMin = verticalAxis === 'u' ? wall.uMin : wall.vMin;
    const verticalMax = verticalAxis === 'u' ? wall.uMax : wall.vMax;
    // The floor sits at one end of the wall's local vertical extent. Extend the
    // doorway toward the far end, which is the lower coordinate on walls whose
    // local up axis is mirrored (the left wall runs from 0 down to -height).
    const farEnd =
      Math.abs(floorVertical - verticalMin) <= Math.abs(floorVertical - verticalMax)
        ? verticalMax
        : verticalMin;
    const dir = Math.sign(farEnd - floorVertical) || 1;
    const bottom = floorVertical;
    const top = bottom + dir * portal.height;
    const va = Math.min(bottom, top);
    const vb = Math.max(bottom, top);

    if (uIsVertical) {
      return {
        uMin: Math.min(va, vb),
        uMax: Math.max(va, vb),
        vMin: centre.v - portal.width / 2,
        vMax: centre.v + portal.width / 2,
      };
    }
    return {
      uMin: centre.u - portal.width / 2,
      uMax: centre.u + portal.width / 2,
      vMin: Math.min(va, vb),
      vMax: Math.max(va, vb),
    };
  }

  private doorFloorSegment(wall: Surface, hole: SurfaceHole): FloorSegment | null {
    const corners: Array<[number, number]> = [
      [hole.uMin, hole.vMin],
      [hole.uMax, hole.vMin],
      [hole.uMin, hole.vMax],
      [hole.uMax, hole.vMax],
    ];
    const world = corners.map(([u, v]) => wall.toWorld(u, v, 0, new Vector3()));
    world.sort((a, b) => a.y - b.y);
    const floor = this.surfaces.get(this.keyFor(wall.zoneId, 'floor'));
    if (!floor) return null;
    const p0 = floor.toLocal(world[0], { u: 0, v: 0 });
    const p1 = floor.toLocal(world[1], { u: 0, v: 0 });
    const du = Math.abs(p0.u - p1.u);
    const dv = Math.abs(p0.v - p1.v);
    // The segment varies along one axis; the edge line itself lies on the
    // other axis at the constant coordinate.
    if (du >= dv) {
      return {
        axis: 'v',
        value: (p0.v + p1.v) / 2,
        alongMin: Math.min(p0.u, p1.u),
        alongMax: Math.max(p0.u, p1.u),
      };
    }
    return {
      axis: 'u',
      value: (p0.u + p1.u) / 2,
      alongMin: Math.min(p0.v, p1.v),
      alongMax: Math.max(p0.v, p1.v),
    };
  }

  private addPortalEdge(
    owner: Surface,
    own: FloorSegment,
    targetSurface: Surface,
    target: FloorSegment,
  ): void {
    // Remove the wall seam from the floor where the door sits, then add a
    // portal edge in its place. The wall seam still exists beside the door.
    const kept: SurfaceEdge[] = [];
    for (const edge of owner.edges) {
      if (edge.kind === 'portal') {
        kept.push(edge);
        continue;
      }
      const sameLine = edge.axis === own.axis && Math.abs(edge.value - own.value) < 0.01;
      if (!sameLine) {
        kept.push(edge);
        continue;
      }
      const overlapMin = Math.max(edge.alongMin, own.alongMin);
      const overlapMax = Math.min(edge.alongMax, own.alongMax);
      if (overlapMax <= overlapMin) {
        kept.push(edge);
        continue;
      }
      if (edge.alongMin < overlapMin - 1e-6) {
        kept.push({ ...edge, alongMax: overlapMin });
      }
      if (edge.alongMax > overlapMax + 1e-6) {
        kept.push({ ...edge, alongMin: overlapMax });
      }
    }
    owner.edges.length = 0;
    owner.edges.push(...kept);

    const centre = own.axis === 'u' ? (owner.uMin + owner.uMax) / 2 : (owner.vMin + owner.vMax) / 2;
    const sign: 1 | -1 = own.value >= centre ? 1 : -1;
    owner.edges.push({
      neighbor: targetSurface.key,
      axis: own.axis,
      value: own.value,
      alongMin: own.alongMin,
      alongMax: own.alongMax,
      sign,
      kind: 'portal',
      targetAxis: target.axis,
      targetValue: target.value,
      targetAlongMin: target.alongMin,
      targetAlongMax: target.alongMax,
    });
  }

  // ------------------------------------------------------------------ lookup

  getSurface(ref: string): Surface | undefined {
    const direct = this.surfaces.get(ref);
    if (direct) return direct;
    if (!this.keyed) return undefined;
    return this.surfaces.get(this.keyFor(this.defaultZone, ref as SurfaceId));
  }

  /** All surfaces that can be attached to (magnetic and enabled). */
  get attachableSurfaces(): Surface[] {
    return [...this.surfaces.values()].filter((surface) => surface.magnetic);
  }

  /** The zone that most fully contains `point`, or null when there are none. */
  zoneAt(point: Vector3): string | null {
    if (!this.keyed) return this.defaultZone;
    let best: string | null = null;
    let bestDepth = -Infinity;
    for (const zone of this.zones) {
      let depth = Infinity;
      for (const face of ALL_SURFACE_IDS) {
        const surface = this.surfaces.get(this.keyFor(zone.id, face));
        if (!surface) continue;
        depth = Math.min(depth, surface.signedDistance(point));
      }
      if (depth > bestDepth) {
        bestDepth = depth;
        best = zone.id;
      }
    }
    return best;
  }

  /** Keep a free-flying body inside its current zone. */
  constrain(position: Vector3, velocity: Vector3, clearance: number): void {
    const zoneId = this.zoneAt(position);
    if (!zoneId) return;
    if (!this.keyed) {
      for (const surface of this.surfaces.values()) {
        constrainAgainst(surface, position, velocity, clearance);
      }
      return;
    }
    for (const face of ALL_SURFACE_IDS) {
      const surface = this.surfaces.get(this.keyFor(zoneId, face));
      if (surface) constrainAgainst(surface, position, velocity, clearance);
    }
  }

  /**
   * Push a point inside the zone that contains it, without touching a velocity.
   *
   * Used by the third-person camera: the old loop tested every surface in the
   * level, so a camera in one zone was shoved through the wall by the next
   * zone's surfaces. Only the containing zone is authoritative.
   */
  pushInside(position: Vector3, clearance: number): void {
    const zoneId = this.zoneAt(position);
    if (!zoneId) return;
    if (!this.keyed) {
      for (const surface of this.surfaces.values()) pushClearance(surface, position, clearance);
      return;
    }
    for (const face of ALL_SURFACE_IDS) {
      const surface = this.surfaces.get(this.keyFor(zoneId, face));
      if (surface) pushClearance(surface, position, clearance);
    }
  }
}

/**
 * True when a point sits over an opening in a surface, where there is no wall
 * to be pushed out of. Doorways are the way between zones, so forcing a body
 * out of the shared wall everywhere would make them impassable in mid air.
 */
function overOpening(surface: Surface, position: Vector3): boolean {
  if (surface.holes.length === 0) return false;
  const local = surface.toLocal(position, scratchLocal);
  return surface.containsHole(local.u, local.v);
}

function pushClearance(surface: Surface, position: Vector3, clearance: number): void {
  const distance = surface.signedDistance(position);
  if (distance >= clearance) return;
  if (overOpening(surface, position)) return;
  position.addScaledVector(surface.normal, clearance - distance);
}

function constrainAgainst(
  surface: Surface,
  position: Vector3,
  velocity: Vector3,
  clearance: number,
): void {
  const distance = surface.signedDistance(position);
  if (distance >= clearance) return;
  if (overOpening(surface, position)) return;
  const correction = clearance - distance;
  position.addScaledVector(surface.normal, correction);
  const intoSurface = velocity.dot(surface.normal);
  if (intoSurface < 0) velocity.addScaledVector(surface.normal, -intoSurface);
}

/**
 * The edge the body is approaching, if it is within `band` of it and moving
 * towards it.
 *
 * The corner blend starts here, before the body reaches the edge, so the
 * corner reads as an arc rather than a snap at the seam.
 */
export function findApproachingEdge(
  surface: Surface,
  u: number,
  v: number,
  velocityU: number,
  velocityV: number,
  band: number,
): { edge: SurfaceEdge; alongValue: number } | null {
  let best: { edge: SurfaceEdge; alongValue: number } | null = null;
  let bestDistance = Infinity;

  for (const edge of surface.edges) {
    // Doors are crossed, not cornered: only the exact threshold transfers.
    if (edge.kind === 'portal') continue;
    const coordinate = edge.axis === 'u' ? u : v;
    const velocity = edge.axis === 'u' ? velocityU : velocityV;
    // The neighbour lies at greater coordinates when the sign is positive.
    const towards = edge.sign > 0 ? velocity > 0 : velocity < 0;
    if (!towards) continue;

    const distance = edge.sign > 0 ? edge.value - coordinate : coordinate - edge.value;
    if (distance < 0 || distance > band) continue;
    if (distance >= bestDistance) continue;

    // Only approach an edge where the body is actually next to it. A doorway
    // splits the wall seam, and the body must not corner-blend onto the wall
    // while it is lined up with the door.
    const rawAlong = edge.axis === 'u' ? v : u;
    if (rawAlong < edge.alongMin - 0.05 || rawAlong > edge.alongMax + 0.05) continue;

    bestDistance = distance;
    best = {
      edge,
      alongValue: Math.min(Math.max(rawAlong, edge.alongMin), edge.alongMax),
    };
  }

  return best;
}

/**
 * Pick the edge a point has crossed, if any. Handles corners by clamping.
 *
 * The deepest crossing wins. When two edges are crossed by the same amount
 * (a corner) the tie is broken by the neighbour's position in
 * `ALL_SURFACE_IDS`, so the result never depends on the order the edges
 * happen to be stored in or on floating point noise. Doorways win over a
 * coincident wall seam.
 */
export function findCrossedEdge(
  surface: Surface,
  u: number,
  v: number,
): { edge: SurfaceEdge; alongValue: number } | null {
  let best: { edge: SurfaceEdge; alongValue: number } | null = null;
  let bestDepth = -Infinity;
  let bestRank = Infinity;

  for (const edge of surface.edges) {
    const coordinate = edge.axis === 'u' ? u : v;
    const outside = edge.sign > 0 ? coordinate > edge.value : coordinate < edge.value;
    if (!outside) continue;

    // A doorway only transfers a body that is actually in front of the
    // opening. Without this, crossing the wall line anywhere along the wall
    // would pick the portal (it outranks the face seam) and teleport the body
    // through solid geometry.
    if (edge.kind === 'portal') {
      const rawAlong = edge.axis === 'u' ? v : u;
      if (rawAlong < edge.alongMin - 0.1 || rawAlong > edge.alongMax + 0.1) continue;
    }

    const depth = Math.abs(coordinate - edge.value);
    const rank =
      edge.kind === 'portal' ? -1 : ALL_SURFACE_IDS.indexOf(faceNameOf(edge.neighbor) as SurfaceId);
    const deeper = depth > bestDepth + 1e-9;
    const tied = Math.abs(depth - bestDepth) <= 1e-9 && rank < bestRank;
    if (!deeper && !tied) continue;

    bestDepth = depth;
    bestRank = rank;
    const alongValue = edge.axis === 'u' ? v : u;
    best = {
      edge,
      alongValue: Math.min(Math.max(alongValue, edge.alongMin), edge.alongMax),
    };
  }

  return best;
}

const EPS = 1e-6;
const scratchCorner = new Vector3();
const scratchLocalPoint = { u: 0, v: 0 };

/** The lowest world coordinate of a zone on an axis (zone origin is the floor). */
function zoneMin(zone: ZoneInput, axis: 'x' | 'y' | 'z'): number {
  return axis === 'y' ? zone.origin.y : zone.origin[axis] - zone.size[axis] / 2;
}

/** The highest world coordinate of a zone on an axis. */
function zoneMax(zone: ZoneInput, axis: 'x' | 'y' | 'z'): number {
  return axis === 'y' ? zone.origin.y + zone.size.y : zone.origin[axis] + zone.size[axis] / 2;
}

/**
 * The plane a zone face sits on, and which way points out of the zone.
 *
 * Zone origins are floor centres, so the floor sits at the origin and the
 * ceiling at origin.y + height.
 */
function facePlane(
  zone: ZoneInput,
  face: SurfaceId,
): { axis: 'x' | 'y' | 'z'; at: number; beyond: 1 | -1 } | null {
  const { x, y, z } = zone.origin;
  switch (face) {
    case 'left':
      return { axis: 'x', at: x - zone.size.x / 2, beyond: -1 };
    case 'right':
      return { axis: 'x', at: x + zone.size.x / 2, beyond: 1 };
    case 'front':
      return { axis: 'z', at: z - zone.size.z / 2, beyond: -1 };
    case 'back':
      return { axis: 'z', at: z + zone.size.z / 2, beyond: 1 };
    case 'floor':
      return { axis: 'y', at: y, beyond: -1 };
    case 'ceiling':
      return { axis: 'y', at: y + zone.size.y, beyond: 1 };
    default:
      return null;
  }
}

/** `rect` minus `cover`, as up to four rectangles. */
function subtractRect(rect: SurfaceHole, cover: SurfaceHole): SurfaceHole[] {
  const uMin = Math.max(rect.uMin, cover.uMin);
  const uMax = Math.min(rect.uMax, cover.uMax);
  const vMin = Math.max(rect.vMin, cover.vMin);
  const vMax = Math.min(rect.vMax, cover.vMax);
  if (uMax - uMin < EPS || vMax - vMin < EPS) return [rect];

  const pieces: SurfaceHole[] = [];
  if (rect.vMin < vMin) pieces.push({ ...rect, vMax: vMin });
  if (rect.vMax > vMax) pieces.push({ ...rect, vMin: vMax });
  if (rect.uMin < uMin) pieces.push({ uMin: rect.uMin, uMax: uMin, vMin, vMax });
  if (rect.uMax > uMax) pieces.push({ uMin: uMax, uMax: rect.uMax, vMin, vMax });
  return pieces;
}
